import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_KEY = "ambient-sound-enabled";
const POSITION_KEY = "ambient-sound-position";
const POSITION_SAVE_INTERVAL_MS = 2000;
const TARGET_VOLUME = 0.22;
const FADE_STEP = 0.015;
const FADE_INTERVAL_MS = 60;

/** Gestures browsers accept as user activation for unmuted playback. */
const ACTIVATION_EVENTS: (keyof DocumentEventMap)[] = [
  "pointerdown",
  "keydown",
  "touchend",
];

const readStoredPreference = (): boolean => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === null ? true : stored === "true";
  } catch {
    return true;
  }
};

const writeStoredPreference = (value: boolean) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    /* storage blocked — preference just won't persist */
  }
};

const readStoredPosition = (): number => {
  try {
    const stored = Number(window.sessionStorage.getItem(POSITION_KEY));
    return Number.isFinite(stored) && stored > 0 ? stored : 0;
  } catch {
    return 0;
  }
};

/**
 * Loops a background track for as long as the visitor stays on the site.
 * Browsers block unmuted autoplay, so when the initial play() is rejected we
 * wait for the visitor's first click/tap/keypress and start from there.
 */
export const useAmbientSound = (src: string) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fadeTimerRef = useRef<number | null>(null);
  const [enabled, setEnabled] = useState<boolean>(readStoredPreference);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAvailable, setIsAvailable] = useState(true);

  useEffect(() => {
    const audio = new Audio(src);
    audio.loop = true;
    audio.preload = "none";
    audio.volume = 0;
    audioRef.current = audio;

    const handleError = () => setIsAvailable(false);

    // Pick the track back up where the last page view left it, so a reload
    // or an internal link doesn't restart the loop from 0:00.
    const handleLoadedMetadata = () => {
      const savedPosition = readStoredPosition();
      if (savedPosition > 0 && savedPosition < audio.duration) {
        audio.currentTime = savedPosition;
      }
    };

    let lastSavedAt = 0;
    const savePosition = () => {
      try {
        window.sessionStorage.setItem(POSITION_KEY, String(audio.currentTime));
      } catch {
        /* storage blocked — playback just restarts next time */
      }
    };
    const handleTimeUpdate = () => {
      const now = Date.now();
      if (now - lastSavedAt < POSITION_SAVE_INTERVAL_MS) return;
      lastSavedAt = now;
      savePosition();
    };

    audio.addEventListener("error", handleError);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata, { once: true });
    audio.addEventListener("timeupdate", handleTimeUpdate);
    window.addEventListener("pagehide", savePosition);

    return () => {
      savePosition();
      audio.removeEventListener("error", handleError);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      window.removeEventListener("pagehide", savePosition);
      audio.pause();
      audioRef.current = null;
    };
  }, [src]);

  const clearFade = useCallback(() => {
    if (fadeTimerRef.current !== null) {
      window.clearInterval(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  }, []);

  const fadeTo = useCallback(
    (target: number, onComplete?: () => void) => {
      const audio = audioRef.current;
      if (!audio) return;

      clearFade();
      fadeTimerRef.current = window.setInterval(() => {
        const current = audioRef.current;
        if (!current) {
          clearFade();
          return;
        }

        const remaining = target - current.volume;
        if (Math.abs(remaining) <= FADE_STEP) {
          current.volume = target;
          clearFade();
          onComplete?.();
          return;
        }

        current.volume = Math.min(
          1,
          Math.max(0, current.volume + Math.sign(remaining) * FADE_STEP),
        );
      }, FADE_INTERVAL_MS);
    },
    [clearFade],
  );

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !isAvailable) return;

    if (!enabled) {
      fadeTo(0, () => audio.pause());
      setIsPlaying(false);
      return;
    }

    let cancelled = false;

    const stopWaitingForGesture = () => {
      ACTIVATION_EVENTS.forEach((eventName) =>
        document.removeEventListener(eventName, handleGesture),
      );
    };

    const start = () => {
      const attempt = audio.play();

      if (attempt === undefined) {
        setIsPlaying(true);
        fadeTo(TARGET_VOLUME);
        return;
      }

      attempt
        .then(() => {
          if (cancelled) return;
          setIsPlaying(true);
          fadeTo(TARGET_VOLUME);
          stopWaitingForGesture();
        })
        .catch(() => {
          // Autoplay blocked — retry once the visitor interacts with the page.
          if (cancelled) return;
          ACTIVATION_EVENTS.forEach((eventName) =>
            document.addEventListener(eventName, handleGesture, { once: true }),
          );
        });
    };

    function handleGesture() {
      if (cancelled) return;
      stopWaitingForGesture();
      start();
    }

    start();

    return () => {
      cancelled = true;
      stopWaitingForGesture();
      clearFade();
    };
  }, [enabled, isAvailable, fadeTo, clearFade]);

  // Don't keep playing to an empty room while the tab sits in the background.
  useEffect(() => {
    const handleVisibilityChange = () => {
      const audio = audioRef.current;
      if (!audio) return;

      if (document.hidden) {
        clearFade();
        audio.pause();
        return;
      }

      if (!enabled || !isAvailable) return;
      audio
        .play()
        .then(() => fadeTo(TARGET_VOLUME))
        .catch(() => {
          /* still waiting on user activation */
        });
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [enabled, isAvailable, fadeTo, clearFade]);

  const toggle = useCallback(() => {
    setEnabled((previous) => {
      const next = !previous;
      writeStoredPreference(next);
      return next;
    });
  }, []);

  return { enabled, isPlaying, isAvailable, toggle };
};

export default useAmbientSound;
